import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiSearch, FiFilter, FiEye, FiCalendar, FiBarChart2, FiPlus } from 'react-icons/fi';
import { datosAPI, catalogosAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import storageService from '../services/storageService';

const Historicidad = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [datos, setDatos] = useState([]);
  const [datosLocales, setDatosLocales] = useState([]);
  const [catalogos, setCatalogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCatalogo, setSelectedCatalogo] = useState('');
  const [ordenar, setOrdenar] = useState('reciente');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    cargarCatalogosYConteos();
    cargarDatosLocales();
  }, []);

  useEffect(() => {
    cargarDatos();
  }, [searchTerm, selectedCatalogo, ordenar, page]);

  const cargarDatosLocales = async () => {
    if (!user) return;
    
    try {
      await storageService.init();
      const locals = await storageService.historicalData.obtenerTodos();
      console.log('🏛️ Datos históricos locales cargados:', locals.length);
      setDatosLocales(locals);
    } catch (error) {
      console.error('Error cargando datos locales:', error);
    }
  };

  const cargarCatalogosYConteos = async () => {
    try {
      // Cargar catálogos
      const catalogosResponse = await catalogosAPI.listar();
      const catalogosList = catalogosResponse.data.catalogos || catalogosResponse.data || [];
      
      // Cargar todos los datos para calcular conteos
      const datosResponse = await datosAPI.getPublicados({ page: 1, limit: 1000 });
      const todosDatos = datosResponse.data.datos || [];
      
      // Calcular conteos
      const conteos = {};
      todosDatos.forEach((dato) => {
        if (dato.catalogos && Array.isArray(dato.catalogos)) {
          dato.catalogos.forEach((cat) => {
            conteos[cat.id] = (conteos[cat.id] || 0) + 1;
          });
        }
      });

      // Actualizar catálogos con conteos
      const catalogosConConteos = catalogosList.map((cat) => ({
        ...cat,
        conteoDatos: conteos[cat.id] || 0,
      }));
      
      setCatalogos(catalogosConConteos);
    } catch (error) {
      console.error('Error al cargar catálogos y conteos:', error);
      setCatalogos([]);
    }
  };

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 12,
        busqueda: searchTerm,
        ordenar
      };

      // Solo agregar filtro de catálogo si hay uno seleccionado
      if (selectedCatalogo && selectedCatalogo !== '') {
        params.catalogo = selectedCatalogo;
      }

      const response = await datosAPI.getPublicados(params);
      setDatos(response.data.datos || []);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      setDatos([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  console.log("--->JT 1111, los datos que llegaron son:", datos);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-4">Historicidad Bíblica</h1>
              <p className="text-purple-100 text-lg">
                Explora datos históricos verificados sobre eventos, personajes y lugares bíblicos
              </p>
            </div>
            {user && (
              <button
                onClick={() => navigate('/historicidad/mis-datos')}
                className="px-6 py-3 bg-white text-purple-600 rounded-lg font-semibold hover:bg-purple-50 transition-colors shadow-lg flex items-center gap-2"
              >
                <FiEye className="w-5 h-5" />
                Mis Datos
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Barra de búsqueda y filtros */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Búsqueda */}
            <div className="flex-1">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Buscar datos históricos..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Filtro por catálogo */}
            <div className="md:w-64">
              <div className="relative">
                <FiFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <select
                  value={selectedCatalogo}
                  onChange={(e) => {
                    setSelectedCatalogo(e.target.value);
                    setPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none"
                >
                  <option value="">Todos los catálogos</option>
                  {catalogos.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.nombre} ({cat.conteoDatos || 0})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Ordenar */}
            <div className="md:w-48">
              <select
                value={ordenar}
                onChange={(e) => {
                  setOrdenar(e.target.value);
                  setPage(1);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="reciente">Más recientes</option>
                <option value="antiguos">Más antiguos</option>
                <option value="masVistos">Más vistos</option>
                <option value="historicidad">Mayor historicidad</option>
              </select>
            </div>
          </div>
        </div>

        {/* Datos locales del usuario (si tiene) */}
        {user && datosLocales.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              🏛️ Mis Datos Históricos Locales
              <span className="text-sm font-normal text-gray-500">({datosLocales.length})</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8 p-4 bg-purple-50 rounded-lg">
              {datosLocales.map((dato) => (
                <DatoLocalCard 
                  key={`local_${dato.id}`} 
                  dato={dato}
                  onDelete={() => cargarDatosLocales()}
                  onEdit={() => navigate(`/historicidad/editar/local_${dato.id}`)}
                />
              ))}
            </div>
          </div>
        )}

        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          📚 Datos Publicados
        </h2>

        {/* Grid de datos */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : datos.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-500 text-lg">No se encontraron datos históricos</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {datos.map((dato) => (
                <div
                  key={dato.id}
                  className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden"
                >
                  {dato.imagen && (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={dato.imagen}
                        alt={dato.tema}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}

                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">
                      {dato.tema}
                    </h3>

                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                      {dato.descripcion}
                    </p>

                    {/* Porcentajes */}
                    <div className="grid grid-cols-3 gap-2 mb-4 text-xs">
                      <div className="text-center">
                        <div className="font-semibold text-blue-600">
                          {/*dato.porcentajeHistoricidad?.toFixed(0)*/}
                          {dato.porcentajeHistoricidad}%
                        </div>
                        <div className="text-gray-500">Historicidad</div>
                      </div>
                      <div className="text-center">
                        <div className="font-semibold text-green-600">
                          {/*dato.porcentajeAutenticidad?.toFixed(0)*/}
                          {dato.porcentajeAutenticidad}%
                        </div>
                        <div className="text-gray-500">Autenticidad</div>
                      </div>
                      <div className="text-center">
                        <div className="font-semibold text-purple-600">
                          {/*dato.porcentajeVeracidad?.toFixed(0)*/}
                          {dato.porcentajeVeracidad}%
                        </div>
                        <div className="text-gray-500">Veracidad</div>
                      </div>
                    </div>

                    {/* Catálogos */}
                    {dato.catalogos && dato.catalogos.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-4">
                        {dato.catalogos.slice(0, 2).map((catalogo) => (
                          <span
                            key={catalogo.id}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                            style={{
                              backgroundColor: `${catalogo.color}20`,
                              color: catalogo.color
                            }}
                          >
                            {catalogo.nombre}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Metadata */}
                    <div className="flex items-center justify-between text-sm text-gray-500 border-t pt-4">
                      <div className="flex items-center space-x-3">
                        {dato.fechaEvento && (
                          <span className="flex items-center text-xs">
                            <FiCalendar className="mr-1" size={12} />
                            {new Date(dato.fechaEvento).getFullYear()}
                          </span>
                        )}
                        <span className="flex items-center text-xs">
                          <FiEye className="mr-1" size={12} />
                          {dato.visualizaciones || 0}
                        </span>
                      </div>

                      <Link
                        to={`/historicidad/${dato.slug}`}
                        className="text-purple-600 hover:text-purple-800 font-medium text-sm"
                      >
                        Ver más →
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Paginación */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Anterior
                </button>

                <span className="text-gray-700">
                  Página {page} de {totalPages}
                </span>

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Siguiente
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Botón flotante para crear dato histórico (solo usuarios registrados) */}
      {user && (
        <div className="fixed bottom-8 right-8 z-30">
          <button
            onClick={() => navigate('/historicidad/crear')}
            className="w-14 h-14 sm:w-16 sm:h-16 bg-purple-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 flex items-center justify-center"
            title="Crear dato histórico"
          >
            <FiPlus className="w-6 h-6 sm:w-8 sm:h-8" />
          </button>
        </div>
      )}
    </div>
  );
};

// Componente para tarjeta de dato local
function DatoLocalCard({ dato, onDelete, onEdit }) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!confirm('¿Eliminar este dato histórico local?')) return;
    
    setDeleting(true);
    try {
      await storageService.historicalData.eliminar(dato.id);
      if (onDelete) onDelete(dato.id);
      alert('✅ Dato eliminado');
    } catch (error) {
      console.error('Error eliminando dato:', error);
      alert('Error al eliminar el dato');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden">
      {dato.imagen && (
        <div className="h-48 overflow-hidden">
          <img
            src={dato.imagen}
            alt={dato.tema}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div className="p-6">
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-xl font-bold text-gray-900 line-clamp-2 flex-1">
            {dato.tema}
          </h3>
          <span className="ml-2 text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full font-medium whitespace-nowrap">
            💾 Local
          </span>
        </div>

        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
          {dato.descripcion}
        </p>

        {dato.fechaEvento && (
          <div className="flex items-center text-xs text-gray-500 mb-4">
            <FiCalendar className="mr-1" size={12} />
            {new Date(dato.fechaEvento).getFullYear()}
          </div>
        )}

        {/* Estado y botones */}
        <div className="flex items-center justify-between border-t pt-4">
          <div className="text-xs text-gray-500">
            {dato.estado === 'borrador_local' ? (
              <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                Borrador
              </span>
            ) : dato.estado === 'enviado' ? (
              <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">
                Enviado
              </span>
            ) : null}
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={onEdit}
              disabled={deleting}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Editar"
            >
              <FiEye size={16} />
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
              title="Eliminar"
            >
              {deleting ? '...' : <FiBarChart2 size={16} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Historicidad;

